import { SharedNodeTitles } from "@p0security/shared/assessment/constants";
import { IdentityNode } from "@p0security/shared/types/assessment/data/index";
import { Flex, Typography } from "@p0security/ui";
import { ExportColumnType } from "components/Assessment/utils/export";
import pluralize from "pluralize";

import {
  StaleCredentialAggregate,
  UsedCredentialAggregate,
} from "../../cells/Credential";
import { CredentialTitle } from "../../cells/CredentialTitle";
import { IdentityCell, identityCellValue } from "../../cells/Identity";
import {
  IdentityAccess,
  IdentityAccessTitle,
  hasIdentityAccessWarning,
} from "../../cells/IdentityAccess";
import { LastAuthenticated } from "../../cells/LastAuthenticated";
import { RiskAggregate, riskSorter } from "../../cells/RiskAggregate";
import { epochMillisExport, genericExport } from "../../export";
import { numberSorter, stringSorter } from "../../sort";
import { AssessmentColumnProps } from "../columns";
import { providerTitleElements } from "../grant/shared";
import { credentialExportFromIdentity, identityLookback } from "./IdentityNode";
import { credentialsByStaleness, credentialsByUsage } from "./grouping";
import {
  credentialsFor,
  identityTypesWithCredentials,
  lastAuthnTimeFor,
} from "./shared";

const replaceNaN = (value: number, fallback: number) =>
  isNaN(value) ? fallback : value;

const accessWarningList = (node: IdentityNode) => {
  const { mfa, publicJoin, domainJoin, memberAdd, memberApprove } =
    hasIdentityAccessWarning(node);
  const warnings = [];
  if (mfa) warnings.push("MFA disabled");
  if (publicJoin) warnings.push("Anyone can join");
  if (domainJoin) warnings.push("Anyone in domain can join");
  if (memberAdd) warnings.push("Anyone in group can add");
  if (memberApprove) warnings.push("Anyone in group can approve join requests");
  return warnings;
};

const lastAuthnForIdentity = (node: IdentityNode) =>
  lastAuthnTimeFor(credentialsFor(node));

export const identityColumns = (
  props: AssessmentColumnProps
): ExportColumnType<IdentityNode>[] => [
  {
    key: "identity",
    title: SharedNodeTitles.identity,
    ellipsis: true,
    sorter: (left, right) => stringSorter(left.key, right.key),
    render: (_, node) => (
      <div className="flex flex-col">
        <IdentityCell identity={node.data} {...props} />
        {!node.data.external && (
          <Typography.Text
            type="secondary"
            /* eslint-disable-line react/forbid-component-props */ style={{
              fontSize: "small",
            }}
          >
            {node.data.parent ?? ""}
          </Typography.Text>
        )}
      </div>
    ),
    export: genericExport((node) => identityCellValue(node.data)),
    width: 200,
  },
  {
    key: "lastAuthnTime",
    title: "Last Used",
    ellipsis: true,
    sorter: (left, right) =>
      // Ordering is reversed because it looks back in time
      numberSorter(
        replaceNaN(lastAuthnForIdentity(right) ?? 0, 0),
        replaceNaN(lastAuthnForIdentity(left) ?? 0, 0)
      ),
    render: (_, node) => (
      <LastAuthenticated
        identity={node.data}
        last={lastAuthnForIdentity(node)}
        lookback={identityLookback(credentialsFor(node))}
      />
    ),
    export: epochMillisExport((node) => lastAuthnForIdentity(node), undefined),
    width: 130,
  },
  {
    key: "credentials",
    title: <CredentialTitle />,
    render: (_, node) => {
      const credentials = credentialsFor(node);
      return (
        <Flex align="flex-start" gap="1em">
          <UsedCredentialAggregate
            credentials={
              identityTypesWithCredentials.includes(node.data.type)
                ? credentialsByUsage(credentials)
                : credentialsByUsage([])
            }
            {...props}
          />
          <StaleCredentialAggregate
            credentials={
              identityTypesWithCredentials.includes(node.data.type)
                ? credentialsByStaleness(credentials)
                : credentialsByStaleness([])
            }
            {...props}
          />
        </Flex>
      );
    },
    export: genericExport(credentialExportFromIdentity),
    width: 200,
  },
  {
    key: "access",
    title: <IdentityAccessTitle />,
    sorter: (left, right) =>
      numberSorter(
        accessWarningList(left).length,
        accessWarningList(right).length
      ),
    render: (_, node) => <IdentityAccess node={node} {...props} />,
    export: genericExport((node) => accessWarningList(node)),
    width: 100,
  },
  {
    key: "grant",
    title: pluralize.plural(providerTitleElements[props.provider].grant),
    sorter: (left, right) =>
      numberSorter(
        left.aggregates.grants.length,
        right.aggregates.grants.length
      ),
    render: (_, node) => node.aggregates.grants.length,
    export: genericExport((node) => node.aggregates.grants.map((g) => g.data)),
    width: 120,
    minWidth: 120,
  },
  {
    key: "risk",
    title: pluralize.plural(SharedNodeTitles.risk),
    sorter: (left, right) =>
      riskSorter(left.aggregates.risks, right.aggregates.risks),
    render: (_, node) => <RiskAggregate risks={node.aggregates.risks} />,
    export: genericExport((node) => node.aggregates.risks),
    width: 100,
    minWidth: 90,
  },
];
